import { ExtendCurrency, ExtendLocales } from 'src/enums'
import type { CurrencyFormatterPart } from 'src/localization/currencyFormatter'
import {
  createCurrencyFormatter,
  createCurrencyPartsFormatter,
} from 'src/localization/currencyFormatter'
import type { TranslateFn } from 'src/localization/translate'

import apiTranslationKeyAliases from './pricingApiTranslationKeyAliases.json'
import apiTranslationPatterns from './pricingApiTranslationPatterns.json'

export interface PricingPresentationAdapter {
  currencyCode: ExtendCurrency
  formatCurrencyAmount: (amount: number) => string
  formatCurrencyAmountParts: (
    amount: number,
    maximumFractionDigits?: number,
  ) => CurrencyFormatterPart[]
  translateApiText: (source: string) => string
  translateQuantityMaxUsers: (quantityMax: number) => string
}

export const PRICING_API_EXPLICIT_TRANSLATION_KEYS = [
  'Starter',
  'Pro',
  'Enterprise',
  'For small businesses',
  'Maximum 5 users',
  'Up to 5 users',
  'Up to ${quantityMax} users',
  'Up to 100 virtual cards per month*',
  '1 expense category and 15 labels',
  'Upload receipts',
  'Download transaction data and receipts',
  'For small-medium sized businesses',
  'Minimum billing: ${minimumBillingUsers} users',
  'Everything in Starter, plus:',
  'Pay per user',
  'Up to 500 virtual cards per month*',
  'Physical card transactions feed',
  'Advanced receipt management',
  'Expanded expense categories, labels, and automatic coding',
  'Automated monthly expense reports',
  'QuickBooks Online integration',
  'For large scale businesses who need customization',
  'No user minimum',
  'Everything in Pro, plus additional options:',
  'Unlimited users',
  'Automate virtual card issuance with API key*',
  'Unlimited expense categories, labels, and automatic coding',
  'Custom reporting and implementation',
  'Dedicated account manager',
  'NetSuite integration',
  'Users',
  'Active Virtual Cards',
  'Virtual Cards',
  'Expense Categories',
  'Guest Users',
  '1 expense category',
  'Up to 10 guest seats',
  'Up to 50 guest seats',
  'Up to 100 active virtual cards',
  'Up to 100 virtual cards per month',
  'Up to 500 active virtual cards',
  'Up to 500 virtual cards per month',
  'Unlimited virtual cards per month',
  'Unlimited active virtual cards',
  'Unlimited guest seats',
  'Expanded expense categories, labels & coding',
  'Unlimited expense categories, labels & coding',
  'Expense additional fields',
  'AI assistant',
  'AI Policy Assistant',
  'Automated expense reports',
  'QuickBooks integration',
  'Xero integration',
  'Client Integration',
  'Bill Pay',
  'Bulk Credit Card Enrollment',
  'Sage Intacct integration',
  'Manager assignment',
  'Approval workflows',
  'Business rules',
  'Expense category rules',
  'AI expense categories',
  'Automate virtual card issuance with API key',
  'Netsuite Integration',
  'Dynamics Business Central Integration',
  'Mobile wallet',
  'Budgets',
  'Customer support',
  'SSO',
  'User roles and permissions',
  'For medium businesses',
  'For large-scale, customized solutions',
  'Minimum billing: 10 users',
  'Get started',
  'Start Free 30-day trial',
  'Get Started',
  'Contact Us',
  'Continue with Starter',
  'Continue with Pro',
  'Continue with Enterprise',
  'Save 25% with annual billing',
  'Save ${savingsPercent}%',
  'Popular',
  'Custom pricing',
  'Users & Administration',
  'Cards & Spend Controls',
  'Expense Management',
  'Receipts',
  'Accounting & Integrations',
  'World Legend',
  'Mid-Market Accelerator',
  'WORLD LEGEND MEMBER BENEFITS',
  'MEMBER BENEFITS',
  '2 free Pro seats for your first year — on us',
  '10 free Pro seats for your first year — on us',
  '2 free Pro seats for your first year',
  '10 free Pro seats for your first year',
  'Applied automatically.',
  'Exclusive savings designed to help your business grow.',
  'Exclusive member pricing is applied automatically.',
  'YOUR FIRST-YEAR PRICE',
  '20% off your first year — billed monthly, 10-seat team',
  '36% off your first year — billed monthly, 10-seat team',
  '100% off your first year — billed monthly, 10-seat team',
  '100% off your first year — billed annually, 10-seat team',
  'You pay',
  'billed monthly with member benefits ★ vs',
  'billed annually with member benefits ★ vs',
  'billed monthly with Mid-Market Accelerator benefits ★ vs',
  'billed annually with Mid-Market Accelerator benefits ★ vs',
  'billed monthly with World Legend Member benefits ★ vs',
  'billed annually with World Legend Member benefits ★ vs',
  '2 seats free',
  '10 seats free',
  '2 free seats included, first year',
  '10 free seats included, first year',
] as const

const explicitTranslationKeys = new Set<string>(
  PRICING_API_EXPLICIT_TRANSLATION_KEYS,
)

const API_TRANSLATION_KEY_ALIASES: Readonly<
  Partial<Record<string, string>>
> = Object.assign(Object.create(null), apiTranslationKeyAliases)
// The pattern's issuer allowlist mirrors extend-api's ISSUER_PLAN_MAPPING.
// Matching arbitrary partners would translate copy the inventory has not reviewed.
const PROMOTION_SAVINGS_BADGE_PATTERN = new RegExp(
  apiTranslationPatterns.promotionSavingsBadge.sourcePattern,
)

export function createPricingPresentation(
  translate: TranslateFn,
  locale: ExtendLocales = ExtendLocales.enUS,
): PricingPresentationAdapter {
  const formatUsd = createCurrencyFormatter(locale, ExtendCurrency.Usd)
  const formatUsdParts = createCurrencyPartsFormatter(
    locale,
    ExtendCurrency.Usd,
  )

  return {
    currencyCode: ExtendCurrency.Usd,
    formatCurrencyAmount: (amount) =>
      formatUsd({
        amount,
        currency: ExtendCurrency.Usd,
        forceShowCurrency: true,
      }),
    formatCurrencyAmountParts: (amount, maximumFractionDigits = 2) =>
      formatUsdParts({
        amount,
        currency: ExtendCurrency.Usd,
        forceShowCurrency: true,
        maximumFractionDigits,
      }),
    translateApiText: (source) => {
      const promotionSavingsBadgeMatch =
        PROMOTION_SAVINGS_BADGE_PATTERN.exec(source)

      if (promotionSavingsBadgeMatch) {
        if (
          locale === ExtendLocales.enUS ||
          locale === ExtendLocales.enCA
        ) {
          return source
        }

        const [, partner, savingsPercent] = promotionSavingsBadgeMatch
        const translatedSavings = translate(
          apiTranslationPatterns.promotionSavingsBadge.translationKey,
          { savingsPercent },
        )

        return `${partner} - ${translatedSavings}`
      }

      const translationKey = API_TRANSLATION_KEY_ALIASES[source] ?? source

      if (!explicitTranslationKeys.has(translationKey)) return source
      if (
        (locale === ExtendLocales.enUS || locale === ExtendLocales.enCA) &&
        translationKey !== source
      ) {
        return source
      }

      return translate(translationKey)
    },
    translateQuantityMaxUsers: (quantityMax) =>
      quantityMax === 5
        ? translate('Up to 5 users')
        : translate('Up to ${quantityMax} users', {
            quantityMax: String(quantityMax),
          }),
  }
}
